import { lazy, memo, useEffect, useState } from "react";
import PropTypes from "prop-types";

import { useSnackbar } from "src/context/SnackbarContext";

import { Box, Button, Fade, IconButton, MenuItem, Tooltip } from "@mui/material";
import ProgressCirc from "src/components/layout/Progress/ProgressCirc";
import LazyLoader from "src/components/layout/Progress/LazyLoader";
const AreYouSureDialog = lazy(() => import("./AreYouSureDialog"));

// AreYouSureButton
// onClick: function called when button is clicked
// onCancel: function called when no is clicked when asked
// children: button text or icon
// askAreYouSure: if false, onClick is called without asking
// dontAskId: textfield to remember not to ask again, if null, no checkbox is shown
// disabled: if true, button is disabled
// component: button, iconButton, menuItem
// isLoading: if true, show progress circ
// dialogProps: title, content, acceptText, cancelText
// snackbarProps: doneMessage, failMessage, klikMessage
// toolTipProps: props for tooltip
// componentProps: props for component
// helpId: id for help text

const AreYouSureButton = ({
  askAreYouSure = true, // if false, onClick is called without asking
  helpId = null,
  dontAskId = null, // textfield to remember not to ask again, if null, no checkbox is shown
  disabled = false,
  component = "button", // button, iconButton, menuItem
  componentProps = {},
  dialogProps = {}, // title, content, acceptText, cancelText
  snackbarProps = { autoHideDuration: 2000 }, // doneMessage, failMessage, klikMessage
  toolTipProps = {},
  onCancel = null,
  children = null,
  isLoading: isLoadingProp = false, // if true, show progress circ
  onClick, // function called when button is clicked
  disableTabStop = false,
}) => {
  const { openSnackbar } = useSnackbar();

  const [askDialogIsOpen, setAskDialogIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [event, setEvent] = useState(null);
  const isDisabled = isLoading || disabled || componentProps?.disabled || askDialogIsOpen;

  useEffect(() => {
    setIsLoading(isLoadingProp);
  }, [isLoadingProp]);

  const handleButtonClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEvent(e);
    if (askAreYouSure) {
      if (!askDialogIsOpen) setAskDialogIsOpen(true);
    } else {
      await handleConfirm(true);
    }
  };

  const handleConfirm = async (isConfirmed) => {
    if (isConfirmed?.ok) isConfirmed = true;
    else if (isConfirmed?.success) isConfirmed = true;
    setAskDialogIsOpen(false);
    if (isConfirmed) {
      // user clicked ok
      if (snackbarProps?.klikMessage) {
        openSnackbar({
          open: true,
          message: snackbarProps.klikMessage,
          title: snackbarProps.title,
          severity: "info",
          linearProgressBarProps: snackbarProps?.linearProgressBarProps || null,
        });
      }

      if (onClick) {
        try {
          setIsLoading(true);
          const _result = await onClick(event);
          if (_result === undefined) {
            openSnackbar({
              open: true,
              message: (
                <>
                  {snackbarProps?.failMessage}
                  <br />
                  AreYouSureButton onClick event returned undefined
                </>
              ),
              title: snackbarProps.title,
              severity: "error",
              autoHideDuration: null,
            });
          } else if ((_result === true || _result?.success === true) && snackbarProps.doneMessage) {
            openSnackbar({
              open: true,
              message: snackbarProps.doneMessage,
              title: snackbarProps.title,
              severity: "success",
              autoHideDuration: snackbarProps?.autoHideDuration || 2000,
            });
          } else if (_result.success == false || (!_result && snackbarProps?.failMessage)) {
            const Msg = (
              <>
                {snackbarProps?.failMessage}
                <br /> {_result?.message}
                <br /> {_result.error?.message}
              </>
            );
            openSnackbar({
              open: true,
              title: snackbarProps.title,
              message: Msg,
              severity: "error",
              autoHideDuration: null,
            });
          }
        } catch (error) {
          console.log(error);
          console.trace();
          openSnackbar({
            open: true,
            title: snackbarProps.title,
            message: snackbarProps?.failMessage,
            severity: "error",
            autoHideDuration: null,
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        openSnackbar({
          open: true,
          title: "onClick not defined",
          message: "onClick not defined",
          severity: "error",
          autoHideDuration: null,
        });
      }
    } else {
      // user clicked cancel
      if (onCancel) onCancel();
    }
  };

  const renderItemTextContents = () => (
    <div className="center">
      <ProgressCirc isLoading={isLoading} />
      <div className="center">{children}</div>
    </div>
  );

  const renderItemIconContents = () => (
    <>{isLoading ? <ProgressCirc isLoading={isLoading} /> : <>{children}</>}</>
  );

  const Component = {
    button: Button,
    Button: Button,
    menuItem: MenuItem,
    MenuItem: MenuItem,
    iconButton: IconButton,
    IconButton: IconButton,
    Box: Box,
    box: Box,
  }[component];

  const defaultComponentColor = "primary";
  const defaultToolTipPlacement = "bottom-end";

  return (
    <>
      <LazyLoader>
        <AreYouSureDialog
          open={askDialogIsOpen}
          handleAcceptClick={handleConfirm} // false if cancel, true if accept
          helpId={helpId}
          dontAskId={dontAskId}
          {...dialogProps}
        />
      </LazyLoader>
      <Tooltip
        disableInteractive
        placement={toolTipProps?.placement || defaultToolTipPlacement}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 600 }}
        {...toolTipProps}>
        <div
          style={{
            cursor: "pointer",
            width: componentProps?.fullWidth ? "100%" : "auto",
          }}>
          <Component
            tabIndex={disableTabStop ? -1 : 0}
            disabled={isDisabled}
            color={componentProps?.color || defaultComponentColor}
            {...componentProps}
            onClick={handleButtonClick}>
            {component === "iconButton" ? renderItemIconContents() : renderItemTextContents()}
          </Component>
        </div>
      </Tooltip>
    </>
  );
};

export default memo(AreYouSureButton);

AreYouSureButton.propTypes = {
  dialogProps: PropTypes.object,
  snackbarProps: PropTypes.object,
  componentProps: PropTypes.object,
  toolTipProps: PropTypes.object,
  onClick: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  children: PropTypes.node,
  askAreYouSure: PropTypes.bool,
  helpId: PropTypes.string,
  dontAskId: PropTypes.string,
  component: PropTypes.oneOf(["button", "iconButton", "menuItem", "box"]),
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  disableTabStop: PropTypes.bool,
};

/**
 * USAGE
 * 
  <AreYouSureButton
        onClick={handleClick}
        component="button" // button (default), iconButton, menuItem, box
        componentProps={{
          fullWidth: true, // default false
          variant: "outlined", // default "contained"
          "aria-label": "",
          endIcon: <ArrowForwardIcon />, // for button component
        }}
        snackbarProps={{
          title: "Toiminto",
          failMessage: "Toiminto epäonnistui.",
          klikMessage: "Toiminto käynnistetty.",
          doneMessage: "Toiminto suoritettu.", 
          autoHideDuration: 2000,  // default 2000
        }}
        toolTipProps={{
          title: "Käynnistä toiminto.",
          placement: "bottom-end", // default bottom-end
        }}
        disabled={false}
        isLoading={false} // default false

        askAreYouSure={true} // default true (if false, the following props are ignored)
        helpId="helpId" // default null
        dontAskId="dontAskId" // if null, no checkbox is shown
        dialogProps={{
          title: "Oletko varma?",
          content: "Tämä on varmistus dialogi.",
          acceptText: "KYLLÄ",
          cancelText: "PERUUTA",
          ConfirmIcon: <SuccessIcon />,
          CancelIcon: <CancelIcon color="error" />,
        }}
        >
        <>
        NAPPITEKSTI
        </>
      </AreYouSureButton>

 */
